/**
 * Every value this app formats for a person to read.
 *
 * The port of `Infrastructure/Converters.cs`, and the reason it is one module: in the OpenSilver app
 * these were fourteen `IValueConverter` classes referenced from 488 XAML attributes, and the rules
 * they encode - which dates are instants and which are calendar dates, whether a negative amount
 * leads with the sign or wears brackets - are exactly the kind that drift when they live in fourteen
 * places. There is nothing to bind here, so they are functions.
 */

/**
 * Money, the way the CRM has always written it: "$1,234.50", and "-$505.50" for a negative.
 *
 * Sign BEFORE the currency symbol, which is a deliberate difference from the accountant's "(505.50)"
 * used by the Profit and Loss statement. Those statement lines arrive pre-formatted from the server
 * and must be printed verbatim - passing one through here would blank it, because it is a string.
 *
 * Always two decimals. A bare number renders as "145.5", which is how a money column comes to have
 * ragged decimal places, and the `$` in a neighbouring label does not fix it.
 */
const MONEY = new Intl.NumberFormat('en-US', {
  minimumFractionDigits: 2,
  maximumFractionDigits: 2
});

export function money(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  const negative = value < 0;
  return `${negative ? '-' : ''}$${MONEY.format(Math.abs(value))}`;
}

/** Money without the symbol, for a box the caller has already put a `$` beside. */
export function amount(value: number | null | undefined): string {
  if (value === null || value === undefined || Number.isNaN(value)) return '';
  return MONEY.format(value);
}

/**
 * Parses what somebody typed into a money box. Accepts the grouping comma this app writes back, and
 * a leading `$` in case it was pasted.
 *
 * Returns null for anything that is not a number, INCLUDING an empty box - a caller deciding what
 * "nothing" means (zero, unchanged, unset) has to make that decision itself, because the three are
 * genuinely different in the billing code.
 */
export function parseAmount(text: string | null | undefined): number | null {
  if (!text) return null;
  const cleaned = text.replace(/[$,\s]/g, '');
  if (!cleaned || !/^-?\d*\.?\d*$/.test(cleaned)) return null;
  const value = Number(cleaned);
  return Number.isFinite(value) ? value : null;
}

/**
 * An INSTANT, shown in the reader's own timezone: a sign-in, a heartbeat, an audit row.
 *
 * Offset-less strings are read as UTC, which is the one subtlety. LiteDB round-trips can yield a
 * `DateTimeKind.Unspecified`, and System.Text.Json writes no `Z` for one - so a timestamp that IS
 * UTC arrives looking local, and a browser west of Greenwich would render it hours early. The
 * OpenSilver `UtcToLocalDateConverter` made the same assumption for the same reason.
 */
export function localDateTime(value: string | null | undefined): string {
  const date = parseInstant(value);
  return date === null ? '' : date.toLocaleString();
}

/** The same instant, date only. */
export function localDate(value: string | null | undefined): string {
  const date = parseInstant(value);
  return date === null ? '' : date.toLocaleDateString();
}

/** The same instant, time only - for a row whose date is already in its heading. */
export function localTime(value: string | null | undefined): string {
  const date = parseInstant(value);
  return date === null ? '' : date.toLocaleTimeString();
}

/**
 * A CALENDAR DATE, printed rather than converted: an invoice's issue, due or paid date, a BIOS
 * release date, a driver date.
 *
 * Never constructs a `Date`. These have no time of day, so running one through a timezone conversion
 * moves it a day for every reader west of Greenwich - which is how a due date comes to be reported
 * as a day early by exactly the people who care about it. Taking the first ten characters is
 * therefore not laziness, it is the whole point.
 */
export function plainDate(value: string | null | undefined): string {
  if (!value) return '';
  return value.length >= 10 ? value.slice(0, 10) : value;
}

/** Bytes, at a scale a person reads: "4.0 KB", "1.4 GB". */
export function bytes(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  if (value < 1024) return `${Math.round(value)} B`;
  const units = ['KB', 'MB', 'GB', 'TB', 'PB'];
  let scaled = value / 1024;
  let unit = 0;
  while (scaled >= 1024 && unit < units.length - 1) {
    scaled /= 1024;
    unit++;
  }
  return `${scaled.toFixed(scaled < 10 ? 1 : 0)} ${units[unit]}`;
}

/** "3 devices" / "1 device" - the plural rule this app writes dozens of times. */
export function plural(count: number, one: string, many = `${one}s`): string {
  return `${count} ${count === 1 ? one : many}`;
}

/**
 * How long ago, for a "last seen" line. Coarse on purpose: a device seen 40 seconds ago and one seen
 * 50 are the same fact, and a number that ticks draws the eye to something that has not changed.
 */
export function ago(value: string | null | undefined): string {
  const date = parseInstant(value);
  if (date === null) return '';
  const seconds = Math.max(0, (Date.now() - date.getTime()) / 1000);
  if (seconds < 60) return 'just now';
  const minutes = Math.floor(seconds / 60);
  if (minutes < 60) return `${plural(minutes, 'minute')} ago`;
  const hours = Math.floor(minutes / 60);
  if (hours < 24) return `${plural(hours, 'hour')} ago`;
  const days = Math.floor(hours / 24);
  if (days < 30) return `${plural(days, 'day')} ago`;
  return localDate(value);
}

/**
 * How much longer, for a deadline - a lockout, a maintenance window.
 *
 * Says how much longer rather than when, deliberately: an absolute time has to be rendered in some
 * timezone, and a UTC clock time on a sign-in page is how somebody concludes they are locked out
 * until tomorrow.
 */
export function remaining(value: string | null | undefined): string {
  const date = parseInstant(value);
  if (date === null) return '';
  const seconds = (date.getTime() - Date.now()) / 1000;
  if (seconds <= 0) return 'expired';
  const minutes = Math.ceil(seconds / 60);
  if (minutes < 60) return `${plural(minutes, 'minute')} left`;
  const hours = Math.round(minutes / 60);
  if (hours < 48) return `${plural(hours, 'hour')} left`;
  return `${plural(Math.round(hours / 24), 'day')} left`;
}

/** A percentage, one decimal where it earns one: "21.4%", "50%". */
export function percent(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return `${Number.isInteger(value) ? value : value.toFixed(1)}%`;
}

/** Hours, as the billing pages write them: "5.25". Not money, so no symbol and no grouping. */
export function hours(value: number | null | undefined): string {
  if (value === null || value === undefined || !Number.isFinite(value)) return '';
  return value.toFixed(2).replace(/\.?0+$/, '');
}

/**
 * Joins the parts of a composed line, dropping the ones that are absent.
 *
 * Every detail line in this app is a list of facts that may not be there, so the separators cannot
 * live in the layout: a missing fact would leave a dangling dot. Returns "" when nothing survives,
 * which is what a caller tests to decide whether to show the label at all.
 */
export function join(parts: Array<string | null | undefined>, separator = ' · '): string {
  return parts.filter((p): p is string => typeof p === 'string' && p.length > 0).join(separator);
}

/** Truncates for a one-line row, with a real ellipsis. */
export function trim(text: string | null | undefined, max: number): string {
  if (!text) return '';
  return text.length <= max ? text : `${text.slice(0, Math.max(0, max - 1))}…`;
}

/**
 * An ISO-8601 instant as a `Date`, or null.
 *
 * Exported because the charts need the number, not the text, and because the UTC assumption above is
 * the kind of rule that must have exactly one implementation.
 */
export function parseInstant(value: string | null | undefined): Date | null {
  if (!value) return null;
  // No zone marker and no offset means UTC - see localDateTime.
  const text = /[zZ]$|[+-]\d\d:?\d\d$/.test(value) ? value : `${value}Z`;
  const date = new Date(text);
  return Number.isNaN(date.getTime()) ? null : date;
}
